import axios from 'axios';
import { api } from 'constants/constant';

// Axios client specifically for talking to the MiraLabs v2 backend
// Stytch-backed authentication endpoints.
const authApi = axios.create({
  baseURL: api, // e.g. http://localhost:8001/api/v1
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Derive client + branch slugs from the current hostname.
// Examples (dev):
//   fundmore.local.test           -> clientSlug=fundmore, branchSlug=null
//   calvert-east.fundmore.local.test -> clientSlug=fundmore, branchSlug=calvert-east
export const getTenantSlugsFromHost = () => {
  if (typeof window === 'undefined') {
    return { clientSlug: null, branchSlug: null };
  }

  const hostname = (window.location.hostname || '').toLowerCase();
  const branchSeparator = (import.meta?.env?.VITE_TENANT_BRANCH_SEPARATOR || '').toLowerCase();

  // Prefer explicit tenant hints from the URL query, mirroring frontend_v2
  const searchParams = new URLSearchParams(window.location.search || '');
  let clientSlug = (searchParams.get('slug') || '').toLowerCase() || null;
  let branchSlug =
    (searchParams.get('branch') ||
      searchParams.get('branch_slug') ||
      searchParams.get('branchSlug') ||
      '').toLowerCase() || null;

  // Fallback to sessionStorage cache so branch context survives navigation
  if (!clientSlug && typeof sessionStorage !== 'undefined') {
    const cachedClient = sessionStorage.getItem('mira_client_slug');
    if (cachedClient) {
      clientSlug = cachedClient.toLowerCase();
    }
  }
  if (!branchSlug && typeof sessionStorage !== 'undefined') {
    const cachedBranch = sessionStorage.getItem('mira_branch_slug');
    if (cachedBranch) {
      branchSlug = cachedBranch.toLowerCase();
    }
  }

  const parts = hostname.split('.').filter(Boolean);

  // Determine how many trailing segments belong to the base domain.
  // Support multiple base domains (comma-separated) with single-value fallback.
  // e.g., VITE_TENANT_BASE_DOMAINS="uat-fundmore.miralabs.ai,man-uat-pos.fundmore.ai"
  const allBaseDomains = [
    ...(import.meta?.env?.VITE_TENANT_BASE_DOMAINS || '').toLowerCase().split(',').filter(Boolean),
    (import.meta?.env?.VITE_TENANT_BASE_DOMAIN || '').toLowerCase().replace(/^\./, ''),
  ].filter(Boolean);

  // Try to match hostname against known base domains
  let baseSegmentCount = 2; // default
  for (const base of allBaseDomains) {
    const bp = base.split('.').filter(Boolean);
    if (parts.length > bp.length && parts.slice(-bp.length).join('.') === bp.join('.')) {
      baseSegmentCount = bp.length;
      break;
    }
  }

  // Derive from hostname when not present in query
  if (!clientSlug || (!branchSlug && parts.length > baseSegmentCount)) {
    if (parts.length <= baseSegmentCount) {
      // localhost or simple host – treat entire host (before TLD) as client slug
      const base = parts.length === 1 ? parts[0] : parts.slice(0, -1).join('.');
      clientSlug = clientSlug || (base || null);
    } else {
      const pre = parts.slice(0, -baseSegmentCount);
      if (pre.length === 1) {
        const combined = pre[0];
        const separator = branchSeparator || (combined.includes('--') ? '--' : '');
        if (!branchSlug && separator && combined.includes(separator)) {
          const [branchPart, orgPart] = combined.split(separator, 2);
          if (branchPart && orgPart) {
            branchSlug = branchSlug || branchPart;
            clientSlug = clientSlug || orgPart;
          }
        }
        clientSlug = clientSlug || combined;
      } else {
        // For hosts like calvert-east.fundmore.local.test
        clientSlug = clientSlug || pre[pre.length - 1];
        branchSlug = branchSlug || pre[0];
      }
    }
  }

  return { clientSlug: clientSlug || null, branchSlug: branchSlug || null };
};

// Build a callback URL that preserves tenant context so the backend
// can resolve the correct client/branch during magic-link exchange.
export const buildTenantCallbackUrl = (flow = 'b2b') => {
  if (typeof window === 'undefined') {
    // Fallback for non-browser environments
    return `/auth/callback?flow=${encodeURIComponent(flow)}`;
  }

  const baseOrigin = window.location.origin;
  const { clientSlug, branchSlug } = getTenantSlugsFromHost();
  const url = new URL('/auth/callback', baseOrigin);

  if (flow) {
    url.searchParams.set('flow', flow);
  }
  if (clientSlug) {
    url.searchParams.set('slug', clientSlug);
  }
  if (branchSlug) {
    // Branch-scoped flows (e.g., branch portals / B2C branch context)
    url.searchParams.set('branch', branchSlug);
  }

  return url.toString();
};

// Attach tenant headers (organization + branch slug) similar to frontend_v2 apiClient.
authApi.interceptors.request.use(
  (config) => {
    const { clientSlug, branchSlug } = getTenantSlugsFromHost();

    if (clientSlug) {
      // Compatible with backend dev overrides (x-tenant-slug)
      config.headers['X-Tenant-Slug'] = clientSlug;
    }

    if (branchSlug) {
      config.headers['X-Branch-Slug'] = branchSlug;
    }

    if (typeof window !== 'undefined' && window.location?.hostname) {
      config.headers['X-Tenant-Host'] = window.location.hostname;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Discover organizations for a given email (optional UX for multi-tenant flows)
export const discoverOrganizations = (email) =>
  authApi.post('/auth/discovery/organizations', { email });

// Check if the user is a member of a specific organization.
// Mirrors backend_v2 /auth/check-membership contract.
export const checkMembership = (email, organizationId) =>
  authApi.post('/auth/check-membership', {
    email,
    organization_id: organizationId,
  });

// Resolve canonical login portal for an email (consumer vs specialist).
export const resolveLogin = (email) =>
  authApi.post('/auth/resolve-login', { email });

// Send a Stytch magic link to the user.
// payload shape (minimal):
// {
//   email_address: string,
//   organization_id?: string,
//   login_redirect_url: string,
//   signup_redirect_url?: string,
//   ...additional context
// }
export const sendMagicLink = (payload) =>
  authApi.post('/auth/magic-link/send', payload);

// Convenience helper for B2C consumer login using a single organization.
// Uses the same magic-link endpoint as B2B but injects a fixed organization id
// and points back to our /auth/callback handler with flow=b2c.
export const sendConsumerMagicLink = (email) => {
  const organizationId = import.meta.env.VITE_APP_ORGANISATION_ID;

  if (!organizationId) {
    throw new Error(
      'Consumer magic link is not configured. Please set VITE_APP_ORGANISATION_ID in your environment.'
    );
  }

  // Build tenant-aware callback URL so backend can resolve client/branch.
  const callbackUrl = buildTenantCallbackUrl('b2c');

  const payload = {
    email_address: email,
    organization_id: organizationId,
    login_redirect_url: callbackUrl,
    signup_redirect_url: callbackUrl,
  };

  return sendMagicLink(payload);
};

// Exchange a Stytch token from the magic-link callback for a session cookie.
// The exact payload shape should mirror backend_v2's /auth/session/exchange contract.
export const exchangeSession = (payload) =>
  authApi.post('/auth/session/exchange', payload, { withCredentials: true });

// Fetch the current authenticated user from the backend using the session cookie.
export const fetchCurrentUser = () =>
  authApi.get('/auth/me', { withCredentials: true });

// Optional: revoke the current session on logout.
export const revokeSession = () => authApi.post('/auth/session/revoke', {});

// Complete onboarding for B2B employee (Stytch-based).
// Mirrors backend_v2 /b2b/invitations/complete-onboarding contract.
export const completeOnboarding = (payload) =>
  authApi.post('/b2b/invitations/complete-onboarding', payload);

// Complete onboarding for B2C consumer (Stytch Consumer API).
// Mirrors backend_v2 /b2c/auth/complete-onboarding contract.
export const completeConsumerOnboarding = (payload) =>
  authApi.post('/b2c/auth/complete-onboarding', payload);

// Fetch the current B2C consumer user from the backend.
// Uses the dedicated B2C /me endpoint.
export const fetchCurrentConsumer = () => authApi.get('/b2c/auth/me');

// Revoke B2C consumer session on logout.
export const revokeConsumerSession = () =>
  authApi.post('/b2c/auth/session/revoke', {});
